import {Component, DoCheck, OnInit, ViewChild} from '@angular/core';
import {CommonModule, NgForOf, NgIf} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {AlertModalService, ConfirmationModalService} from '../../../common/component/modal';
import {LookupService} from '../../../common/service/lookup.service';
import {MapModeService} from '../../services/map-mode.service';
import {MapToolsService} from '../../services/map-tools.service';
import {SpatialUtilService} from '../../services/spatial-util.service';
import {WidgetConfigLookup} from '../../../common/models/widget-config.model';
import {ViewModeResolution, ViewModes} from '../../../common/models/design-properties.model';
import {NgEventBus} from 'ng-event-bus';
import {catchError, of, tap} from 'rxjs';
import {switchMap} from 'rxjs/operators';
import {WidgetService} from '../../services/widget.service';
import {AppConfigService} from '../../../common/service/app-config.service';
import {CdkDrag, CdkDragHandle, DragRef, Point} from '@angular/cdk/drag-drop';
import {HELP_LINK_CONTENTS_BASE_URL} from '../../../common/constants';
import {HelpLinkComponent} from '../../../help/help-link.component';

export interface DisplayModeOption {
    value: string;
    displayValue: string;
    toolTip: string;
    id: string;
}

@Component({
    selector: 'nlr-display-mode',
    imports: [CommonModule, FormsModule, CdkDrag, CdkDragHandle, HelpLinkComponent],
    providers: [CommonModule],
    templateUrl: './display-mode.component.html',
    styleUrl: './display-mode.component.scss'
})
export class DisplayModeComponent implements OnInit, DoCheck {
    @ViewChild('widget')
    widgetElement: HTMLElement;

    title = 'DisplayModeComponent';

    widgetId = 'displayMode';

    widgetConfig: WidgetConfigLookup = {};

    displayModeOptions: DisplayModeOption[] = [];

    displayModeResolutions: ViewModeResolution[] = [];

    displayModeSelected = '';

    displayModeResolutionSelected = '';

    displayModeToolMap = {};

    viewModes: ViewModes;

    widgetVisibleAtLastCheck: boolean;

    helpLinkUrl: string = HELP_LINK_CONTENTS_BASE_URL + 'display-mode-help.html';

    helpPageTitle: string = 'Attribute Viewer | Display Mode Tool';

    constructor(
        private alertModalService: AlertModalService,
        private confirmationModalService: ConfirmationModalService,
        private appConfigService: AppConfigService,
        private eventBus: NgEventBus,
        private lookupService: LookupService,
        private mapModeService: MapModeService,
        private mapToolsService: MapToolsService,
        private spatialUtilService: SpatialUtilService,
        private widgetService: WidgetService
    ) {}

    ngOnInit() {
        console.debug(`${this.title} - ngOnInit`);

        this.widgetConfig = this.lookupService.getLookups('widgetConfig').find((l: WidgetConfigLookup) => l.name === this.widgetId);

        this.viewModes = this.appConfigService.getDesignProperties().VIEW_MODES;

        this.displayModeOptions.push(
            {
                value: this.viewModes.BLACK_BACKGROUND_MODE,
                displayValue: 'Black background',
                toolTip: 'Black background display mode',
                id: 'blackBackgroundRadio'
            },
            {
                value: this.viewModes.WHITE_BACKGROUND_MODE,
                displayValue: 'White background',
                toolTip: 'White background display mode',
                id: 'whiteBackgroundRadio'
            },
            {
                value: this.viewModes.PRINT_PREVIEW_MODE,
                displayValue: 'Print preview',
                toolTip: 'Print preview display mode',
                id: 'printPreviewRadio'
            }
        );

        this.displayModeSelected = this.mapModeService.viewMode;

        this.displayModeToolMap[this.viewModes.BLACK_BACKGROUND_MODE] = 'DISPLAY_MODE_BLACK';
        this.displayModeToolMap[this.viewModes.PRINT_PREVIEW_MODE] = 'DISPLAY_MODE_PRINT_PREVIEW';
        this.displayModeToolMap[this.viewModes.WHITE_BACKGROUND_MODE] = 'DISPLAY_MODE_WHITE';

        this.displayModeResolutions.push(...this.appConfigService.getDesignProperties().VIEW_MODE_RESOLUTIONS);

        this.displayModeResolutionSelected = this.mapModeService.viewModeResolution;
    }

    ngDoCheck() {
        if (this.widgetElement && !this.widgetVisibleAtLastCheck) {
            this.widgetService.setupWidget(this.widgetId);
        }
        this.widgetVisibleAtLastCheck = !!this.widgetElement;
    }

    computeDragPosition = (userPointerPosition: Point, dragRef: DragRef, dimensions: DOMRect): Point => {
        return this.widgetService.computeDragPosition(userPointerPosition, dragRef, dimensions);
    };

    toggleWidgetVisibility() {
        this.widgetService.onClick('DisplayMode');
        if (this.isWidgetActive()) {
            this.displayModeSelected = this.mapModeService.viewMode;
            this.displayModeResolutionSelected = this.mapModeService.viewModeResolution;
        }
    }

    isWidgetActive() {
        return this.widgetService.isWidgetActive('DisplayMode');
    }

    getTooltip() {
        return this.mapToolsService.getTooltipWithShortcut('DISPLAY_MODE_WINDOW', 'Change the display mode (black, white and print preview)');
    }

    isWidgetDisabled() {
        return this.widgetService.isWidgetDisabled('DisplayMode', this.mapModeService.mapMode);
    }

    isResolutionDisabled() {
        return this.displayModeSelected === this.viewModes.BLACK_BACKGROUND_MODE || this.displayModeSelected === this.viewModes.WHITE_BACKGROUND_MODE;
    }

    displayModeChanged() {
        console.debug(`${this.title} - displayModeChanged`);

        if (this.displayModeSelected === this.viewModes.PRINT_PREVIEW_MODE) {
            if (this.spatialUtilService.hasEdits()) {
                const subscription = this.confirmationModalService
                    .showModal({
                        title: 'Save plan?',
                        confirmationText: 'Plan data must be saved before the plan can be previewed.<br>Do you want to save the plan?'
                    })
                    .pipe(
                        switchMap(action => {
                            if (action === 'yes') {
                                return this.spatialUtilService.save().pipe(tap(() => this.applyDisplayModeChange()));
                            }
                            return of(null);
                        }),
                        catchError(() =>
                            this.alertModalService.createAlertMessage(
                                'There was a problem saving spatial data. Please try again. Contact Landgate if the problem persists.',
                                'Ok',
                                'Error'
                            )
                        )
                    )
                    .subscribe();
                this.eventBus.cast('nlr:busy', subscription);
            } else {
                this.applyDisplayModeChange();
            }
        } else {
            this.applyDisplayModeChange();
        }
    }

    private applyDisplayModeChange() {
        this.mapToolsService
            .activate(
                this.displayModeToolMap[this.displayModeSelected],
                () => {
                    this.mapModeService.viewModeChanged(this.displayModeSelected, this.displayModeResolutionSelected);
                    this.eventBus.cast('displayModeChanged', this.displayModeSelected);
                },
                () => {}
            )
            .subscribe();
    }
}
